/**
 * QuestUI.mjs
 * Pattern Quest v2 — 3-slot quest panel UI.
 *
 * Always visible (no show/hide). Shows light / creative / special quest cards.
 */

import { progressText } from './QuestProgress.mjs';

const REWARD_LABELS = {
  stars: r => `+${r.amount} ⭐`,
  overlay: r => r.pool === 'regular' ? '🎁 Overlay' : '✨ Special',
};

const COMPLETION_GLOW_MS = 2500;

export class QuestUI {
  /**
   * @param {HTMLElement} panelEl - the #quest-panel element
   */
  constructor(panelEl) {
    this._panel = panelEl;
    this._cards = {};     // tier → { el, titleEl, progressEl, rewardEl }
    this._glowTimers = {};
    this._init();
  }

  _init() {
    for (const tier of ['light', 'creative', 'special']) {
      const cardEl = this._panel.querySelector(`.quest-card[data-tier="${tier}"]`);
      if (!cardEl) continue;
      this._cards[tier] = {
        el: cardEl,
        titleEl: cardEl.querySelector('.quest-title'),
        progressEl: cardEl.querySelector('.quest-progress'),
        rewardEl: cardEl.querySelector('.quest-reward'),
      };
    }
  }

  /**
   * Set / replace the displayed quest for a tier.
   * @param {string} tier
   * @param {object} quest
   */
  setQuest(tier, quest) {
    const card = this._cards[tier];
    if (!card || !quest) return;

    card.el.classList.remove('completing');
    if (this._glowTimers[tier]) {
      clearTimeout(this._glowTimers[tier]);
      this._glowTimers[tier] = null;
    }

    card.titleEl.textContent = quest.title;
    card.el.dataset.desc = quest.description;
    card.progressEl.textContent = progressText(quest.progress);
    card.rewardEl.textContent = _rewardLabel(quest.reward);
  }

  /**
   * Refresh the progress text for a tier.
   * @param {string} tier
   * @param {object} quest
   */
  updateProgress(tier, quest) {
    const card = this._cards[tier];
    if (!card || !quest) return;
    card.progressEl.textContent = progressText(quest.progress);
  }

  /**
   * Show the completion glow on a card, then reset to new quest.
   * @param {string} tier
   * @param {object} completedQuest
   */
  showCompletion(tier, completedQuest) {
    const card = this._cards[tier];
    if (!card) return;

    card.progressEl.textContent = 'Complete! ✓';
    card.el.classList.add('completing');

    if (this._glowTimers[tier]) clearTimeout(this._glowTimers[tier]);
    this._glowTimers[tier] = setTimeout(() => {
      card.el.classList.remove('completing');
      this._glowTimers[tier] = null;
    }, COMPLETION_GLOW_MS);
  }

  /** Refresh all 3 cards from an active quests map. */
  refresh(active) {
    for (const [tier, quest] of Object.entries(active)) {
      if (quest) this.setQuest(tier, quest);
    }
  }
}

function _rewardLabel(reward) {
  if (!reward) return '';
  const fn = REWARD_LABELS[reward.kind];
  return fn ? fn(reward) : '';
}
